/**
 * DOM target registration & measurement for the tutorial spotlight.
 *
 * Real UI elements opt in with `data-tutorial-target="<id>"` on the actual
 * interactive element — the tour never draws copies of real controls. The
 * same id may legitimately exist on more than one element (the desktop
 * Sidebar and the mobile Drawer render the same nav set); measurement keeps
 * only the VISIBLE instance: laid out (non-zero size) and at least partly
 * inside the horizontal viewport (the closed drawer sits at
 * translateX(-100%), i.e. fully left of the viewport).
 */
import { useEffect, useMemo, useState } from 'react';
import type { SpotRect } from './spotlightGeometry';

export const TUTORIAL_TARGET_ATTR = 'data-tutorial-target';

/** Pure visibility rule (unit-tested): laid out and horizontally on-screen. */
export function isMeasurableRect(rect: SpotRect, viewportW: number): boolean {
  return rect.w > 0 && rect.h > 0 && rect.x + rect.w > 0 && rect.x < viewportW;
}

/** Measure one target id → EVERY visible instance's viewport rect (a legit
 *  plural case: both round-end rows of one team share a target id). */
export function measureDomTargetAll(id: string, root: ParentNode = document): SpotRect[] {
  const nodes = root.querySelectorAll<HTMLElement>(`[${TUTORIAL_TARGET_ATTR}="${id}"]`);
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 0;
  const out: SpotRect[] = [];
  for (const el of Array.from(nodes)) {
    const b = el.getBoundingClientRect();
    const rect: SpotRect = { x: b.left, y: b.top, w: b.width, h: b.height };
    if (isMeasurableRect(rect, viewportW)) out.push(rect);
  }
  return out;
}

/** Measure one target id → the first visible instance's viewport rect, or null. */
export function measureDomTarget(id: string, root: ParentNode = document): SpotRect | null {
  return measureDomTargetAll(id, root)[0] ?? null;
}

/** Scroll the visible instance into view if it is vertically clipped. */
export function scrollDomTargetIntoView(id: string): void {
  const nodes = document.querySelectorAll<HTMLElement>(`[${TUTORIAL_TARGET_ATTR}="${id}"]`);
  for (const el of Array.from(nodes)) {
    const b = el.getBoundingClientRect();
    if (!isMeasurableRect({ x: b.left, y: b.top, w: b.width, h: b.height }, window.innerWidth)) continue;
    if (b.top < 0 || b.bottom > window.innerHeight) {
      el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
    return;
  }
}

/**
 * Live-measured rect for one DOM target id.
 *
 * Re-measures on resize / visualViewport changes / scroll (capture phase —
 * the shell's scroll containers don't bubble), and keeps a short rAF retry
 * loop while the target is missing or mid-animation (drawer slide-in),
 * settling once two consecutive frames agree.
 */
export function useDomTargetRect(id: string | null): SpotRect | null {
  const [rect, setRect] = useState<SpotRect | null>(null);

  useEffect(() => {
    if (!id) {
      setRect(null);
      return;
    }
    let raf = 0;
    let last: SpotRect | null = null;
    let settledFrames = 0;

    const same = (a: SpotRect | null, b: SpotRect | null) =>
      !!a && !!b && Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5 &&
      Math.abs(a.w - b.w) < 0.5 && Math.abs(a.h - b.h) < 0.5;

    const tick = () => {
      const m = measureDomTarget(id);
      if (same(m, last)) {
        settledFrames++;
      } else {
        settledFrames = 0;
        last = m;
        setRect(m);
      }
      // Keep sampling while unfound or still moving; go idle once settled.
      if (settledFrames < 2) raf = requestAnimationFrame(tick);
    };

    const restart = () => {
      settledFrames = 0;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
    };

    scrollDomTargetIntoView(id);
    restart();
    window.addEventListener('resize', restart);
    window.addEventListener('scroll', restart, true);
    window.visualViewport?.addEventListener('resize', restart);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', restart);
      window.removeEventListener('scroll', restart, true);
      window.visualViewport?.removeEventListener('resize', restart);
    };
  }, [id]);

  return rect;
}

/**
 * Live-measured rects for a SET of DOM target ids (plural per id — see
 * measureDomTargetAll). Poll-based like usePhaserTargets: overlay content
 * animates in (round-end rows stagger ~300ms), so gentle sampling keeps the
 * spotlight glued without per-animation wiring. Tutorial-only cost.
 */
export function useDomTargetRects(targetIds: string[]): Record<string, SpotRect[]> {
  const [rects, setRects] = useState<Record<string, SpotRect[]>>({});
  const key = targetIds.join('|');
  const ids = useMemo(() => (key.length ? key.split('|') : []), [key]);

  useEffect(() => {
    if (ids.length === 0) {
      setRects({});
      return;
    }
    const measure = () => {
      const out: Record<string, SpotRect[]> = {};
      for (const id of ids) out[id] = measureDomTargetAll(id);
      setRects((prev) => (JSON.stringify(prev) === JSON.stringify(out) ? prev : out));
    };
    measure();
    const interval = window.setInterval(measure, 300);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      window.visualViewport?.removeEventListener('resize', measure);
      setRects({});
    };
  }, [ids]);

  return rects;
}
